/**
 * Quantity-price BANDS — the one canonical table (Chapter 10, F4).
 *
 * # Why this moved here
 *
 * The band table used to live in `apps/public/src/lib/pricing.ts`, which the
 * admin cannot import. As soon as the admin needs to RENDER the bands (the
 * Chapter-10 tier editor) a second copy appears, and a second copy of a price
 * table is a pricing bug waiting for a deploy: the admin would happily author
 * a key the public resolver never reads. So the table lives in the shared
 * schema package next to `TieredPricesSchema` — the two are views of one fact
 * and are pinned to each other by `pricingBands.test.ts`.
 *
 * # Chapter 10 change (F4)
 *
 * The table stopped at `250+`, so 250 boxes and 2,500 boxes paid the same unit
 * price — no staffel above 250. Two bands are appended: `500-999` and
 * `1000+`.
 *
 * The legacy `250+` KEY is kept verbatim because every flow document in Cosmos
 * carries it; it now denotes the **250-499** band. Nothing should render the
 * raw key any more — `describeBand` exists so no consumer has to know that the
 * key string lies.
 *
 * # No data migration
 *
 * The two new keys are OPTIONAL. `getTieredPrice`'s downward walk resolves an
 * unpriced upper band from the highest priced band below it, so every stored
 * document keeps resolving exactly the price it resolves today (proved by the
 * frozen golden-master test in `apps/public/src/lib/pricing.seededParity.test.ts`).
 */

/** Every band key, ascending. Fixed by the stored data — do not reorder. */
export const TIER_BAND_KEYS = [
  '25-49',
  '50-99',
  '100-149',
  '150-249',
  '250+',
  '500-999',
  '1000+',
] as const;

/** A `TieredPrices` key. */
export type Tier = (typeof TIER_BAND_KEYS)[number];

export interface TierBand {
  /** The `TieredPrices` key. Stored in Cosmos — never rename. */
  readonly key: Tier;
  /** Inclusive lower bound: the smallest quantity in this band. */
  readonly min: number;
  /** Inclusive upper bound; `undefined` on the open-ended top band. */
  readonly max?: number;
  /**
   * Whether `TieredPricesSchema` REQUIRES the key. The five pre-Chapter-10
   * bands are required (every stored doc has them); the two new upper bands
   * are optional so no document needs migrating.
   */
  readonly required: boolean;
}

/**
 * The canonical band table. Single source of truth for the active-tier
 * lookup, the schema's key set (parity-tested), the admin tier editor and the
 * public quantity affordance.
 */
export const TIER_BANDS: readonly TierBand[] = Object.freeze([
  { key: '25-49', min: 25, max: 49, required: true },
  { key: '50-99', min: 50, max: 99, required: true },
  { key: '100-149', min: 100, max: 149, required: true },
  { key: '150-249', min: 150, max: 249, required: true },
  // Reads "250+" but means 250-499 as of Chapter 10. See the header note.
  { key: '250+', min: 250, max: 499, required: true },
  { key: '500-999', min: 500, max: 999, required: false },
  { key: '1000+', min: 1000, required: false },
] as const satisfies readonly TierBand[]);

/** The band a quantity falls in, or `null` below the lowest floor. */
export function getActiveTier(qty: number): Tier | null {
  // Walk from the top: the first band whose floor the quantity meets.
  for (let i = TIER_BANDS.length - 1; i >= 0; i--) {
    const band = TIER_BANDS[i]!;
    if (qty >= band.min) return band.key;
  }
  return null;
}

/** The band entry for a key. */
export function getBand(key: Tier): TierBand {
  const band = TIER_BANDS.find((b) => b.key === key);
  if (!band) {
    // Unreachable while `Tier` is derived from the table — fail loud if the
    // two ever drift at runtime (e.g. a hand-cast string).
    throw new Error(`Unknown price-tier band: ${key}`);
  }
  return band;
}

/** True when `TieredPricesSchema` requires this band's key. */
export function isRequiredBand(key: Tier): boolean {
  return getBand(key).required;
}

/**
 * The human quantity range for a band — "25-49", "250-499", "1000+".
 *
 * Exists because the `250+` key is a lie as of Chapter 10: rendering the raw
 * key would tell a customer that 250 and 2,500 are the same band. Every
 * surface that shows a band to a human (admin tier editor, public quantity
 * hint) resolves the label through here.
 */
export function describeBand(key: Tier): string {
  const band = getBand(key);
  return band.max === undefined ? `${band.min}+` : `${band.min}-${band.max}`;
}
